import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import About from "../Assets/Background/about.svg"
import { Parallax, ParallaxProvider } from 'react-scroll-parallax';

const AboutPage = () => {
  const [activeHobby, setActiveHobby] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);

  const [aboutRef, aboutInView] = useInView({
    threshold: 0.2,
    triggerOnce: false
  });

  const [hobbyRef, hobbyInView] = useInView({
    threshold: 0.2,
    triggerOnce: false
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  const hobbies = [
    {
      title: "Website Development",
      description: "Building applications using modern frameworks and best practices. Focused on Angular/NextJS, ASP.NET, and cloud-native technologies.",
      icon: "💻"
    },
    {
      title: "Network Architect",
      description: "Studying, designing and implementing robust network solutions. Specializing in SDN, network automation, and cloud connectivity.",
      icon: "🌐"
    },
    {
      title: "Cloud Computing",
      description: "Leveraging cloud platforms to build scalable and resilient infrastructure. Expert in AWS, Azure, and multi-cloud architectures.",
      icon: "☁️"
    },
    {
      title: "Machine Learning",
      description: "Exploring AI/ML applications in network optimization and automation. Working with TensorFlow and PyTorch frameworks.",
      icon: "🤖"
    },
    {
      title: "Cybersecurity",
      description: "Implementing security best practices and staying updated with latest threats and mitigation strategies.",
      icon: "🔒"
    },
    {
      title: "Technical Writing",
      description: "Creating comprehensive documentation and technical blogs about network engineering and software development.",
      icon: "📝"
    }
  ];

  const handleMouseMove = (e, hobby) => {
    if (!containerRef.current) return;
    
    const container = containerRef.current.getBoundingClientRect();
    const x = e.clientX - container.left;
    const y = e.clientY - container.top;
    
    // Calculate available space
    const tooltipWidth = 300; // Approximate tooltip width
    const spaceRight = container.width - x;
    
    // Adjust x position if too close to right edge
    const adjustedX = spaceRight < tooltipWidth + 20 ? x - tooltipWidth - 20 : x + 20;
    
    setTooltipPosition({ x: adjustedX, y: y - 20 });
    setActiveHobby(hobby);
  };

  const handleMouseLeave = () => {
    setActiveHobby(null);
  };

  return (
    <>
    <div id="about" className="min-h-screen items-center justify-center mt-5 text-white p-4 md:p-8">
      <ParallaxProvider>
        <div className='absolute top-[50%] w-full items-center right-0 bg-transparent'>

      <Parallax translateY={[100, -100]} scale={[1, 1.05]}>
        <img 
          src={About} 
          alt='about-clipart' 
          className='w-82 h-82 object-contain opacity-15 -z-10 -right-20' 
          />
      </Parallax>
          </div>

      {/* About Section */}
      <motion.section
        ref={aboutRef}
        initial="hidden"
        animate={aboutInView ? "visible" : "hidden"}
        variants={containerVariants}
        className="max-w-6xl mx-auto mb-20"
        >
        <motion.div 
          variants={itemVariants}
          className="mb-8"
          >
          <h1 className="text-4xl md:text-6xl font-[rekind] text-cyan-400 mb-4">
            About Me
          </h1>
          <div className="h-1 w-20 bg-cyan-400 rounded-full mb-8" />
        </motion.div>

        <motion.div
          variants={itemVariants}
          className="grid md:grid-cols-2 gap-8"
          >
          <div className="space-y-6">
            <motion.p 
              className="text-lg md:text-xl leading-relaxed text-xl"
              whileHover={{ scale: 1.00 }}
              transition={{ duration: 0.3 }}
              >
              I'm a passionate technologist with expertise in both networking and software development. My journey in tech has been driven by curiosity and a desire to build meaningful solutions.
            </motion.p>
            <motion.p 
              className="text-lg md:text-xl leading-relaxed text-gray-300 text-xl"
              whileHover={{ scale: 1.00 }}
              transition={{ duration: 0.3 }}
              >
              With a background in network engineering and a love for coding, I bridge the gap between infrastructure and application development.
            </motion.p>
          </div>

          <motion.div 
            className="bg-gradient-to-br from-cyan-900/20 to-cyan-700/20 p-6 rounded-xl border border-cyan-400/20 backdrop-blur-sm"
            whileHover={{ 
              scale: 1.01,
              borderColor: "rgba(34, 211, 238, 0.4)",
              boxShadow: "0 0 20px rgba(34, 211, 238, 0.2)"
            }}
            transition={{ duration: 0.3 }}
            >
            <h2 className="sm:text-xl font-[rekind]  text-cyan-400 mb-4 text-2xl">Skills & Expertise</h2>
            <ul className="space-y-3">
              {["Network Design", "Cloud Architecture", "Full-Stack Development", "DevOps", "Security"].map((skill, index) => (
                <motion.li
                  key={skill}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.01}}
                  className="flex items-center space-x-2 text-xl"
                  >
                  <span className="text-cyan-400">▹</span>
                  <span>{skill}</span>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        </motion.div>
      </motion.section>

      {/* Hobbies & Research Section */}
      <motion.section
        ref={hobbyRef}
        initial="hidden"
        animate={hobbyInView ? "visible" : "hidden"}
        variants={containerVariants}
        className="max-w-6xl mx-auto relative"
        >
        <motion.div variants={itemVariants}>
          <h2 className="text-3xl md:text-5xl font-[rekind] text-cyan-400 mb-4">
            Hobbies & Research
          </h2>
          <div className="h-1 w-20 bg-cyan-400 rounded-full mb-12" />
        </motion.div>

        <motion.div
          ref={containerRef}
          variants={itemVariants}
          className="grid sm:grid-cols-1 grid-cols-3 md:grid-cols-2 mb-40 lg:grid-cols-3 gap-6 relative"
          >
          {hobbies.map((hobby, index) => (
            <motion.div
            key={hobby.title}
            className="p-6 rounded-xl border bg-zinc-900 border-cyan-400/20 bg-gradient-to-br from-cyan-900/10 to-cyan-700/10 backdrop-blur-sm cursor-pointer"
            whileHover={{ 
              scale: 1.05,
              borderColor: "rgba(34, 211, 238, 0.4)",
              boxShadow: "0 0 20px rgba(34, 211, 238, 0.2)"
            }}
            transition={{ duration: 0.2 }}
            initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              onMouseMove={(e) => handleMouseMove(e, hobby)}
              onMouseLeave={handleMouseLeave}
              >
              <div className="flex items-center space-x-3 sm:space-x-1">
                <span className="text-xl sm:text-xl md:text-lg ">{hobby.icon}</span>
                <h3 className="text-xl sm:text-md font-semibold">{hobby.title}</h3>
              </div>
              <div className="h-1 w-10 bg-cyan-400/50 rounded-full mt-2" />
            </motion.div>
          ))}

          <AnimatePresence>
            {activeHobby && (
              <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="fixed z-50 w-72 p-4 rounded-lg bg-gray-800/95 border border-cyan-400/30 backdrop-blur-md shadow-xl"
                style={{
                  left: tooltipPosition.x,
                  top: tooltipPosition.y,
                  pointerEvents: "none"
                }}
                >
                <div className="flex items-center space-x-2 mb-2">
                  <span className="text-xl ">{activeHobby.icon}</span>
                  <h4 className="text-lg font-semibold text-cyan-400">{activeHobby.title}</h4>
                </div>
                <p className="text-sm text-gray-300 leading-relaxed">
                  {activeHobby.description}
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </motion.section>

      {/* Floating Particles Background */}
      <div className="fixed inset-0 -z-10 overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <motion.div
          key={i}
          className="absolute w-2 h-2 bg-cyan-400/20 rounded-full"
          initial={{
            x: Math.random() * window.innerWidth,
            y: Math.random() * window.innerHeight,
          }}
          animate={{
            y: [0, -20, 0],
            opacity: [0.2, 0.5, 0.2],
          }}
          transition={{
            duration: 3 + Math.random() * 2,
            repeat: Infinity,
              ease: "easeInOut",
              delay: Math.random() * 2,
            }}
            />
          ))}
      </div>
          </ParallaxProvider>
    </div>
            </>
  );
};

export default AboutPage;